import { format } from "@react-input/number-format";

const brlFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Placeholder for missing values in tables and detail rows. */
export const EMPTY_VALUE = "-";

/** Formats reais as BRL (`R$ 1.234,50`). Invalid numbers render as `R$ 0,00`. */
export function formatCurrencyBRL(amount: number) {
  return brlFormatter.format(Number.isFinite(amount) ? amount : 0);
}

/** Formats integer cents (how financial tables store money) as BRL. */
export function formatCentsBRL(amountInCents: number) {
  return formatCurrencyBRL(amountInCents / 100);
}

/**
 * Formats a number with configurable decimal places
 * @param value - The number to format
 * @param options - Optional configuration for decimal places
 * @returns The formatted number string
 */
export function formatNumber(
  value: number,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
) {
  // Handle invalid values
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "0";
  }

  // Validate and sanitize fraction digits to prevent errors
  const minFractionDigits = Math.max(
    0,
    Math.min(20, Math.floor(options?.minimumFractionDigits ?? 0)),
  );
  const maxFractionDigits = Math.max(
    minFractionDigits,
    Math.min(20, Math.floor(options?.maximumFractionDigits ?? 0)),
  );

  try {
    return format(value, {
      locales: ["pt-BR"],
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
  } catch {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    }).format(value);
  }
}
